import { useMemo } from 'react';
import { connect } from 'react-redux';
import { CircularProgress, Typography } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { AUTH_LEVELS } from '../../auth/Authorized';
import { defaultTableOptions, defaultTableInitialState } from '../../util/data-table';
import getBuildingStaging from '../../../redux/selectors/staging';
import {
  enrichColHeaderWithTooltip,
  deviceIdCol,
  deviceNameCol,
  deviceProtocolCol,
  deviceModelCol,
  deviceVendorCol,
  deviceDescriptionCol,
  deploymentNameCol,
  firstDiscoveredCol,
  lastDiscoveredCol,
  colBase,
  enrichColAccessorFnWithMissing,
} from '../columns';

const Devices = ({
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  devicesLoadTime,
  stagingDevices,
}) => {
  const columns = useMemo(
    () =>
      [
        deviceIdCol((d) => d.deviceId),
        deviceNameCol((d) => d.name),
        deploymentNameCol((d) => d.deployment?.name),
        deviceProtocolCol((d) => d.protocol),
        deviceModelCol((d) => d.properties?.modelName),
        deviceVendorCol((d) => d.properties?.vendorName),
        deviceDescriptionCol((d) => d.properties?.description),

        { ...colBase, id: 'deviceAddress', accessorKey: 'address', header: 'Device Address' },
        {
          ...colBase,
          id: 'devicePointCount',
          accessorKey: 'pointCount',
          header: 'Point Count',
          filterVariant: 'range',
        },
        firstDiscoveredCol,
        lastDiscoveredCol,
      ]
        .map(enrichColHeaderWithTooltip)
        .map(enrichColAccessorFnWithMissing),
    []
  );

  // This breaks page changes while the page is rendering
  const data = useMemo(
    () =>
      stagingDevices.map((d) => ({
        ...d,
        // eslint-disable-next-line no-underscore-dangle
        address: d.properties?._address,
        pointCount: d.points.length ?? 0,
      })),
    [stagingDevices]
  );

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const devicesSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const devicesColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    enableGrouping: true,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: devicesColumnFilters,
        sorting: devicesSorting,
        // Defining columns we DO want to show by default (defaultTableInitialState will pull in filtered cols)
        hiddenColumns: columns
          .map((c) => c.id)
          .filter(
            (id) =>
              ![
                deviceIdCol().id,
                deviceNameCol().id,
                deviceDescriptionCol().id,
                'devicePointCount',
                lastDiscoveredCol.id,
              ].includes(id)
          ),
      }),
    },
    getRowId: (d) => d.id,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: devicesSorting,
      columnFilters: devicesColumnFilters,
    },
  });

  if (!devicesLoadTime)
    return (
      <Typography>
        Loading <CircularProgress size="24px" aria-label="Loading…" />{' '}
      </Typography>
    );

  return <MaterialReactTable table={mrt} />;
};

const mapStateToProps = (state, { building }) => {
  const {
    user: { userAuthLevel },
    entities: {
      staging: { devicesLoadTime },
    },
  } = state;
  const { stagingDevices } = getBuildingStaging(state, building.id);
  return {
    devicesLoadTime: devicesLoadTime[building.id] ?? false,
    stagingDevices: [...stagingDevices.values()],
    isSuperAdmin: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
    canWrite: userAuthLevel < AUTH_LEVELS.ORG_USER,
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Devices);
